import React, { useEffect, useState, useRef } from 'react';
import { AnimatePresence } from 'framer-motion';
import { PhoneFrame } from './components/PhoneFrame';
import { TopBar } from './components/TopBar';
import { ProgressBar } from './components/ProgressBar';
import { SlideHook } from './slides/SlideHook';
import { SlideBayesian } from './slides/SlideBayesian';
import { SlideSegment } from './slides/SlideSegment';
import { SlideDecision } from './slides/SlideDecision';
import { SlideFuture } from './slides/SlideFuture';
import { SlideLead } from './slides/SlideLead';
import { SlideThanks } from './slides/SlideThanks';
import { SlideReward } from './slides/SlideReward';
import { SlideIntro } from './slides/SlideIntro';
import { SlideScreensaver } from './slides/SlideScreensaver';
import { MapModal } from './components/MapModal';
import { StoryboardModal } from './components/StoryboardModal';
import { VisualStoryboardModal } from './components/VisualStoryboardModal';
type SlideId = 'screensaver' | 'intro' | 'segment' | 'hook' | 'reward_hook' | 'hook_perception' | 'decision' | 'reward_decision' | 'decision_perception' | 'future' | 'reward_future' | 'future_perception' | 'lead' | 'thanks';
const SLIDES: SlideId[] = ['screensaver', 'intro', 'segment', 'hook', 'reward_hook', 'hook_perception', 'decision', 'reward_decision', 'decision_perception', 'future', 'reward_future', 'future_perception', 'lead', 'thanks'];
interface LeadData {
  name: string;
  email: string;
  company: string;
  role: string;
}
interface Responses {
  bottleneck?: string;
  bottleneckPerception?: number;
  segment?: string;
  decisionFactor?: string;
  decisionPerception?: number;
  futureNeed?: string;
  futurePerception?: number;
  lead?: LeadData;
}
// Lookup tables for human-readable labels
const HOOK_LABELS: Record<string, string> = {
  translation: 'Scientific translation',
  manufacturing: 'Manufacturing readiness',
  workflow: 'Workflow coordination',
  scalability: 'Clinical scalability'
};
const DECISION_LABELS: Record<string, string> = {
  expertise: 'Scientific expertise',
  speed: 'Speed & responsiveness',
  integrated: 'Integrated capabilities',
  gmp: 'GMP / regulatory confidence'
};
const FUTURE_LABELS: Record<string, string> = {
  translation: 'Translational expertise',
  manufacturing: 'Manufacturing scalability',
  supply: 'Supply chain reliability',
  integrated: 'Integrated development workflows'
};
// Simulated peer poll distributions
const HOOK_POLL = [{
  id: 'translation',
  label: HOOK_LABELS.translation,
  percent: 27
}, {
  id: 'manufacturing',
  label: HOOK_LABELS.manufacturing,
  percent: 34
}, {
  id: 'workflow',
  label: HOOK_LABELS.workflow,
  percent: 22
}, {
  id: 'scalability',
  label: HOOK_LABELS.scalability,
  percent: 17
}];
const DECISION_POLL = [{
  id: 'expertise',
  label: DECISION_LABELS.expertise,
  percent: 31
}, {
  id: 'speed',
  label: DECISION_LABELS.speed,
  percent: 24
}, {
  id: 'integrated',
  label: DECISION_LABELS.integrated,
  percent: 28
}, {
  id: 'gmp',
  label: DECISION_LABELS.gmp,
  percent: 17
}];
const FUTURE_POLL = [{
  id: 'translation',
  label: FUTURE_LABELS.translation,
  percent: 21
}, {
  id: 'manufacturing',
  label: FUTURE_LABELS.manufacturing,
  percent: 26
}, {
  id: 'supply',
  label: FUTURE_LABELS.supply,
  percent: 18
}, {
  id: 'integrated',
  label: FUTURE_LABELS.integrated,
  percent: 35
}];
export function App() {
  // Main application state
  const [currentIndex, setCurrentIndex] = useState(0);
  const [responses, setResponses] = useState<Responses>({});
  const [isMapOpen, setIsMapOpen] = useState(false);
  const [isStoryboardOpen, setIsStoryboardOpen] = useState(false);
  const [isVisualStoryboardOpen, setIsVisualStoryboardOpen] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const currentSlide = SLIDES[currentIndex];
  const totalProgressSteps = SLIDES.length - 2; // Exclude screensaver and thanks
  const showProgress = currentSlide !== 'thanks' && currentSlide !== 'screensaver';
  const showTopBar = currentSlide !== 'screensaver';
  // Auto-reset to screensaver after 60s of inactivity
  useEffect(() => {
    if (currentSlide === 'screensaver') {
      if (timerRef.current) clearTimeout(timerRef.current);
      return;
    }
    const startTimer = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        reset();
      }, 60000);
    };
    startTimer();
    const handleActivity = () => {
      startTimer();
    };
    window.addEventListener('touchstart', handleActivity);
    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('click', handleActivity);
    window.addEventListener('keydown', handleActivity);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      window.removeEventListener('touchstart', handleActivity);
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('click', handleActivity);
      window.removeEventListener('keydown', handleActivity);
    };
  }, [currentSlide]);
  const nextSlide = () => {
    setCurrentIndex(prev => prev < SLIDES.length - 1 ? prev + 1 : prev);
  };
  const prevSlide = () => {
    setCurrentIndex(prev => prev > 0 ? prev - 1 : prev);
  };
  const handleSelect = <K extends keyof Responses,>(key: K, value: Responses[K], autoAdvanceDelay = 450) => {
    setResponses(prev => ({
      ...prev,
      [key]: value
    }));
    // Use a fresh timeout that guarantees it always advances to the next slide
    setTimeout(() => {
      setCurrentIndex(prev => prev < SLIDES.length - 1 ? prev + 1 : prev);
    }, autoAdvanceDelay);
  };
  const handleLeadSubmit = (data: LeadData) => {
    const final = {
      ...responses,
      lead: data
    };
    setResponses(final);
    console.log('--- SNMMI Lead Captured ---', final);
    nextSlide();
  };
  const handleSkipLead = () => {
    console.log('--- SNMMI Anonymous Data ---', responses);
    nextSlide();
  };
  const reset = () => {
    setResponses({});
    setCurrentIndex(0);
  };
  const bottleneckLabel = responses.bottleneck ? HOOK_LABELS[responses.bottleneck] : undefined;
  const decisionLabel = responses.decisionFactor ? DECISION_LABELS[responses.decisionFactor] : undefined;
  const futureLabel = responses.futureNeed ? FUTURE_LABELS[responses.futureNeed] : undefined;
  const canBack = currentIndex > 0 && currentSlide !== 'thanks' && currentSlide !== 'screensaver' && currentSlide !== 'intro';
  const canSkip = currentSlide !== 'thanks' && currentSlide !== 'lead' && currentSlide !== 'intro' && currentSlide !== 'screensaver';
  return <PhoneFrame data-id="element-0">
      <MapModal isOpen={isMapOpen} onClose={() => setIsMapOpen(false)} data-id="element-1" />
      <StoryboardModal isOpen={isStoryboardOpen} onClose={() => setIsStoryboardOpen(false)} data-id="element-2" />
      <VisualStoryboardModal isOpen={isVisualStoryboardOpen} onClose={() => setIsVisualStoryboardOpen(false)} data-id="element-3" />

      {showTopBar && <TopBar onBack={canBack ? prevSlide : undefined} onSkip={canSkip ? nextSlide : undefined} onOpenMap={() => setIsMapOpen(true)} onOpenStoryboard={() => setIsStoryboardOpen(true)} onOpenVisualStoryboard={() => setIsVisualStoryboardOpen(true)} data-id="element-4" />}

      <div className="flex-1 relative flex flex-col overflow-hidden" data-id="element-5">
        <AnimatePresence mode="wait" data-id="element-6">
          {currentSlide === 'screensaver' && <SlideScreensaver key="screensaver" onStart={nextSlide} data-id="element-7" />}

          {currentSlide === 'intro' && <SlideIntro key="intro" onStart={nextSlide} onOpenMap={() => setIsMapOpen(true)} data-id="element-8" />}

          {currentSlide === 'segment' && <SlideSegment key="segment" selectedValue={responses.segment} onSelect={val => handleSelect('segment', val)} data-id="element-9" />}

          {currentSlide === 'hook' && <SlideHook key="hook" selectedValue={responses.bottleneck} onSelect={val => handleSelect('bottleneck', val)} data-id="element-10" />}

          {currentSlide === 'reward_hook' && <SlideReward key="reward_hook" quoteSource="SNMMI floor perspective" quoteText="The science may be elegant. The workflow rarely is." supportingText="Radiopharmaceutical development increasingly depends on coordination across science, manufacturing, logistics, and execution." heroVideo="https://cdn.magicpatterns.com/uploads/9VYkkmBuJg62H6vNaAeXM1/pep.mp4" pollOptions={HOOK_POLL} userChoice={responses.bottleneck} onContinue={nextSlide} data-id="element-11" />}

          {currentSlide === 'hook_perception' && <SlideBayesian key="hook_perception" headline="How strongly would your peers associate Minerva with helping solve:" answerLabel={bottleneckLabel} selectedValue={responses.bottleneckPerception} onSelect={val => handleSelect('bottleneckPerception', val)} data-id="element-12" />}

          {currentSlide === 'decision' && <SlideDecision key="decision" selectedValue={responses.decisionFactor} onSelect={val => handleSelect('decisionFactor', val)} data-id="element-13" />}

          {currentSlide === 'reward_decision' && <SlideReward key="reward_decision" quoteSource="Program leadership perspective" quoteText="Strong science gets attention. Reliable execution wins programs." supportingText="Scientific excellence matters—but speed, integration, and regulatory confidence often shape partner selection." heroVideo="https://cdn.magicpatterns.com/uploads/5utXVxR7qbqC48fiygvAFo/tumor.mp4" pollOptions={DECISION_POLL} userChoice={responses.decisionFactor} onContinue={nextSlide} data-id="element-14" />}

          {currentSlide === 'decision_perception' && <SlideBayesian key="decision_perception" headline="How strongly do your peers associate Minerva with:" answerLabel={decisionLabel} selectedValue={responses.decisionPerception} onSelect={val => handleSelect('decisionPerception', val)} data-id="element-15" />}

          {currentSlide === 'future' && <SlideFuture key="future" selectedValue={responses.futureNeed} onSelect={val => handleSelect('futureNeed', val)} data-id="element-16" />}

          {currentSlide === 'reward_future' && <SlideReward key="reward_future" quoteSource="Strategic development perspective" quoteText="The future challenge may not be developing better molecules—but developing them with less friction." supportingText="As radiopharma evolves, continuity and coordination may become critical competitive advantages." heroImage="https://cdn.magicpatterns.com/uploads/eCUmdt58qfE9oiXXwHPXoN/Screenshot_2026-05-12_at_11.25.42.png" pollOptions={FUTURE_POLL} userChoice={responses.futureNeed} onContinue={nextSlide} data-id="element-17" />}

          {currentSlide === 'future_perception' && <SlideBayesian key="future_perception" headline="How strongly do your peers associate Minerva with:" answerLabel={futureLabel} selectedValue={responses.futurePerception} onSelect={val => handleSelect('futurePerception', val)} data-id="element-18" />}

          {currentSlide === 'lead' && <SlideLead key="lead" onSubmit={handleLeadSubmit} onSkip={handleSkipLead} onOpenMap={() => setIsMapOpen(true)} data-id="element-19" />}

          {currentSlide === 'thanks' && <SlideThanks key="thanks" onReset={reset} onOpenMap={() => setIsMapOpen(true)} data-id="element-20" />}
        </AnimatePresence>
      </div>

      {showProgress && <ProgressBar currentStep={currentIndex} totalSteps={totalProgressSteps} data-id="element-21" />}
    </PhoneFrame>;
}